import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Callout,
  Label,
  LockIcon,
  MailIcon,
  TextInput,
} from '../components/ui';
import { t } from '../i18n';
import { ApiError, api } from '../services/api';

export function ForgotPasswordPage() {
  const labels = t().forgotPassword;
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setMessage(null);

    if (!email.trim()) {
      setError(t().auth.requiredEmail);
      return;
    }

    setLoading(true);
    try {
      const result = await api.forgotPassword(email.trim());
      setMessage(result.message || labels.sent);
      setEmail('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface p-4 text-ink">
      <div className="w-full max-w-md rounded-xl border border-line bg-panel p-6 shadow-bf-sm sm:p-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-500/15 text-teal-700 dark:text-teal-300">
            <LockIcon className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold">
              {labels.title}
            </h1>
            <p className="text-sm text-muted">{labels.hint}</p>
          </div>
        </div>

        {message ? (
          <div className="space-y-4">
            <Callout variant="success">{message}</Callout>
            <Link
              to="/login"
              className="inline-block text-sm font-medium text-teal-700 hover:underline dark:text-teal-300"
            >
              {labels.backToLogin}
            </Link>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit}>
            {error ? <Callout variant="danger">{error}</Callout> : null}
            <div>
              <Label htmlFor="forgot-email" required>
                {t().auth.email}
              </Label>
              <TextInput
                id="forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                autoFocus
                placeholder={t().auth.emailPlaceholder}
              />
            </div>
            <Button type="submit" className="w-full" loading={loading}>
              <MailIcon className="h-4 w-4" />
              {labels.submit}
            </Button>
            <Link
              to="/login"
              className="block text-center text-sm font-medium text-teal-700 hover:underline dark:text-teal-300"
            >
              {labels.backToLogin}
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
